import {
  Button,
  Group,
  NumberInput,
  Select,
  type SelectProps,
  Title
} from "@mantine/core";
import type { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { DateInput } from "../../../mantine/dates/DateInput/DateInput";
import type { DateRangeConsumptionShare } from "../../../utils/backend-types";
import { backendDateOrDateTimeToLuxonDateTimeOrNull } from "../../../utils/dates/backendDateOrDateTimeToLuxonDateTimeOrNull";
import { luxonDateTimeToBackendDateOrDateTime } from "../../../utils/dates/luxonDateTimeToBackendDateOrDateTime";
import { FormFieldInfoText } from "../../BuildingBlocks/Forms/FormFieldInfoText/FormFieldInfoText";
import "./ConsumptionShareMask.scss";

interface ConsumptionShareMaskProps {
  onAddNewVersion: (data: DateRangeConsumptionShare) => void;
  consumers: Array<{ consumer: number; consumerName: string }>;
  earliestDatePossible?: DateTime;
}

function ConsumptionShareMask({
  onAddNewVersion,
  earliestDatePossible,
  consumers
}: ConsumptionShareMaskProps) {
  const [consumerKey, setConsumerKey] = useState(0);
  const options = consumers.map((consumer) => {
    return {
      value: consumer.consumer.toString(),
      label: consumer.consumerName
    };
  });
  const [consumptionShareMaskData, setConsumptionShareMaskData] =
    useState<DateRangeConsumptionShare>({
      firstDate: earliestDatePossible
        ? luxonDateTimeToBackendDateOrDateTime(earliestDatePossible)
        : null,
      lastDate: null,
      consumerConsumptionShares: []
    });

  useEffect(() => {
    setConsumptionShareMaskData({
      firstDate: earliestDatePossible
        ? luxonDateTimeToBackendDateOrDateTime(earliestDatePossible)
        : null,
      lastDate: null,
      consumerConsumptionShares: []
    });
    setConsumerKey((prevKey) => prevKey + 1);
  }, [earliestDatePossible]);

  function handleChangeConsumerPercentile(
    consumerId: number,
    value: number | undefined
  ) {
    const consumerName =
      options.find((option) => option.value === consumerId.toString())?.label ??
      "";

    let consumerConsumptionPercentile = value?.toString();
    if (
      consumerConsumptionPercentile &&
      !consumerConsumptionPercentile.includes(".")
    ) {
      consumerConsumptionPercentile += ".00";
    }
    const consumerConsumptionShare = {
      consumer: consumerId,
      consumerName,
      consumptionPercentile: consumerConsumptionPercentile
    };

    const consumerConsumptionShares =
      consumptionShareMaskData.consumerConsumptionShares.filter(
        (consumer) => consumer.consumer !== consumerConsumptionShare.consumer
      );

    consumerConsumptionShares.push(consumerConsumptionShare);

    setConsumptionShareMaskData({
      ...consumptionShareMaskData,
      consumerConsumptionShares
    });
  }

  function handleChangeDateRange(firstDate?: string | null) {
    setConsumptionShareMaskData({
      ...consumptionShareMaskData,
      firstDate: firstDate ?? null,
      lastDate: null
    });
  }

  return (
    <div className="ConsumptionShareMaskContainer">
      <Title mb={30} order={3}>
        Neue Version hinzufügen
      </Title>
      <div>
        <ConsumptionShareDateInput
          earliestDatePossible={earliestDatePossible}
          initialDates={{
            startDate: consumptionShareMaskData.firstDate ?? null,
            endDate: consumptionShareMaskData.lastDate ?? null
          }}
          onChange={handleChangeDateRange}
        />
        {consumers.map((consumer) => {
          return (
            <ConsumptionShareRow
              consumerId={consumer.consumer}
              key={consumer.consumerName + consumerKey + consumer.consumer}
              options={options}
              onChange={handleChangeConsumerPercentile}
            />
          );
        })}

        <FormFieldInfoText
          infoText={
            "Die Summe der Verbrauchsanteile sollte in jedem Zeitraum immer 100% ergeben."
          }
        />
        <div className="consumption-share-mask-footer">
          <Button onClick={() => onAddNewVersion(consumptionShareMaskData)}>
            Version hinzufügen
          </Button>
        </div>
      </div>
    </div>
  );
}

interface ConsumptionShareDateInputProps {
  onChange: (startDate?: string | null) => void;
  initialDates: { startDate: string | null; endDate: string | null };
  earliestDatePossible?: DateTime;
}

function ConsumptionShareDateInput({
  onChange,
  initialDates,
  earliestDatePossible
}: ConsumptionShareDateInputProps) {
  return (
    <DateInput
      id="consumption-share-date-range-picker"
      label="Gültigkeitsbeginn*"
      mb="40px"
      minDate={earliestDatePossible}
      value={backendDateOrDateTimeToLuxonDateTimeOrNull(initialDates.startDate)}
      onChange={(value) =>
        onChange(value ? luxonDateTimeToBackendDateOrDateTime(value) : null)
      }
    />
  );
}

interface ConsumptionShareRowProps {
  consumerId: number;
  options?: SelectProps["data"];
  onChange: (consumerId: number, value: number) => void;
}

function ConsumptionShareRow({
  consumerId,
  options,
  onChange
}: ConsumptionShareRowProps) {
  return (
    <Group
      className="consumption-share-row-container"
      justify="space-between"
      mb="xs"
    >
      <div className="consumption-share-consumer">
        <label>Verbraucher</label>
        <Select data={options} disabled value={consumerId.toString()} />
      </div>
      <div className="consumption-share-percentage">
        <label>Verbrauchsanteil in %</label>
        <NumberInput
          decimalScale={2}
          suffix="%"
          onChange={(value) => {
            onChange(
              consumerId,
              typeof value === "string" ? parseFloat(value) : value
            );
          }}
        />
      </div>
    </Group>
  );
}

export { ConsumptionShareMask };
